// src/components/Admin/Dashboard.jsx


import React, { useEffect, useState } from "react";
import {
  Users,
  MessageSquare,
  Clock,
  CheckCircle,
  Search,
  TrendingUp,
  AlertCircle,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

import API_BASE_URL from "../../services/config";

import { authFetch } from "../../auth";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);

  // 🔐 One helper to load all dashboard data with JWT automatically attached
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, questionsRes, usersRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/admin/dashboard/stats`),
        authFetch(`${API_BASE_URL}/api/admin/dashboard/recent-questions`),
        authFetch(`${API_BASE_URL}/api/admin/dashboard/recent-users`),
      ]);

      if (!statsRes.ok) {
        throw new Error(`Stats API failed (${statsRes.status})`);
      }
      if (!questionsRes.ok) {
        throw new Error(
          `Recent questions API failed (${questionsRes.status})`
        );
      }
      if (!usersRes.ok) {
        throw new Error(`Recent users API failed (${usersRes.status})`);
      }

      const [statsData, questionsData, usersData] = await Promise.all([
        statsRes.json(),
        questionsRes.json(),
        usersRes.json(),
      ]);

      setStats(statsData || {});
      setRecentQuestions((questionsData || []).slice(0, 5));
      setRecentUsers((usersData || []).slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const StatCard = ({ title, value, icon: Icon, change, color }) => (
    <div className="bg-white overflow-hidden rounded-lg border border-gray-200 shadow-sm">
      <div className="p-5">
        <div className="flex items-center">
          <div className={`flex-shrink-0 rounded-md p-3 ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                {title}
              </dt>
              <dd className="flex items-baseline">
                <div className="text-2xl font-semibold text-gray-900">
                  {value}
                </div>
                {typeof change === "number" && (
                  <div
                    className={`ml-2 flex items-baseline text-sm font-semibold ${
                      change > 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {change > 0 ? (
                      <ArrowUp className="h-4 w-4" />
                    ) : (
                      <ArrowDown className="h-4 w-4" />
                    )}
                    {Math.abs(change)}%
                  </div>
                )}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Welcome header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, Admin
        </h1>
        <p className="text-gray-600 mt-1">
          Here's what's happening with your community today.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard
          title="Total Users"
          value={stats?.totalUsers?.toLocaleString() || "0"}
          icon={Users}
          change={12}
          color="bg-blue-500"
        />
        <StatCard
          title="Total Questions"
          value={stats?.totalQuestions?.toLocaleString() || "0"}
          icon={MessageSquare}
          change={23}
          color="bg-green-500"
        />
        <StatCard
          title="Pending Questions"
          value={stats?.pendingQuestions?.toLocaleString() || "0"}
          icon={Clock}
          change={5}
          color="bg-yellow-500"
        />
        <StatCard
          title="Answered Questions"
          value={stats?.answeredQuestions?.toLocaleString() || "0"}
          icon={CheckCircle}
          change={18}
          color="bg-purple-500"
        />
        <StatCard
          title="Search Queries"
          value={stats?.totalSearchQueries?.toLocaleString() || "0"}
          icon={Search}
          change={8}
          color="bg-indigo-500"
        />
        <StatCard
          title="Today's Users"
          value={stats?.newUsersToday?.toLocaleString() || "0"}
          icon={TrendingUp}
          change={15}
          color="bg-pink-500"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Questions */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Recent Questions
              </h3>
              <a
                href="/admin/questions"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                View all
              </a>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="flow-root">
              <ul className="-mb-8">
                {recentQuestions.map((question, idx) => (
                  <li key={question.id}>
                    <div className="relative pb-8">
                      {idx !== recentQuestions.length - 1 && (
                        <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200" />
                      )}
                      <div className="relative flex space-x-3">
                        <div className="flex-shrink-0">
                          <div
                            className={`h-8 w-8 rounded-full flex items-center justify-center ${
                              question.status === "PENDING"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            <MessageSquare className="h-4 w-4" />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div>
                            <p className="text-sm text-gray-800 font-medium">
                              {question.title}
                            </p>
                            <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                              {question.body}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <span className="truncate">
                              {question.user?.name || "Anonymous"}
                            </span>
                            <span className="mx-1">•</span>
                            <span>
                              {question.createdAt
                                ? new Date(
                                    question.createdAt
                                  ).toLocaleDateString()
                                : ""}
                            </span>
                            <span className="mx-1">•</span>
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                question.status === "PENDING"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {question.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
                {recentQuestions.length === 0 && (
                  <li className="text-sm text-gray-500 py-4">
                    No recent questions found.
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Actions & Recent Users */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Quick Actions
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-4">
                <a
                  href="/admin/questions/pending"
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-6 w-6 text-yellow-500" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        Answer Pending Questions
                      </p>
                      <p className="text-sm text-gray-500">
                        {stats?.pendingQuestions || 0} waiting
                      </p>
                    </div>
                  </div>
                  <div className="text-indigo-600">→</div>
                </a>
                <a
                  href="/admin/users"
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Users className="h-6 w-6 text-blue-500" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        Manage Users
                      </p>
                      <p className="text-sm text-gray-500">
                        {stats?.newUsersToday || 0} new today
                      </p>
                    </div>
                  </div>
                  <div className="text-indigo-600">→</div>
                </a>
                <a
                  href="/admin/search-history"
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Search className="h-6 w-6 text-indigo-500" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        View Search History
                      </p>
                      <p className="text-sm text-gray-500">
                        {stats?.totalSearchQueries || 0} total searches
                      </p>
                    </div>
                  </div>
                  <div className="text-indigo-600">→</div>
                </a>
              </div>
            </div>
          </div>

          {/* Recent Users */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Recent Users
                </h3>
                <a
                  href="/admin/users"
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                >
                  View all
                </a>
              </div>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-4">
                {recentUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <span className="text-indigo-600 font-medium">
                          {user.name?.charAt(0) || user.email?.charAt(0)}
                        </span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-900">
                          {user.name || "No Name"}
                        </p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : ""}
                    </div>
                  </div>
                ))}
                {recentUsers.length === 0 && (
                  <p className="text-sm text-gray-500">No recent users found.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
